<template>
  <TransitionGroup name="toast" tag="div" class="toastContainer">
    <Toast v-for="(toast, i) in appState.toasts" :key="toast.id" @dismiss="appState.toasts.splice(i, 1)">
      {{ toast.message }}
    </Toast>
  </TransitionGroup>
  <Navbar />
  <div :class="{ 'container': true, 'is-fluid': route.meta.fluidContainer }">
    <section class="section">
      <router-view />
    </section>
  </div>
  <div :class="{ 'modal': true, 'is-active': modalOpen }">
    <div class="modal-background"></div>
    <div class="modal-content" id="modalContent">
      <Modal name="reconnectLoader">
        <div class="loader is-loading reconnectLoader"></div>
      </Modal>
    </div>
    <!-- <button v-if="!appState.modal?.options?.hideClose" class="modal-close is-large" aria-label="close" @click="closeModal" /> -->
  </div>
</template>

<script>
import { defineComponent, provide, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import useAppState from '@/hooks/appState'
import useModal from '@/hooks/useModal'
import useCharacters from '@/hooks/useCharacters'
import { useHost, useJoin } from '@/hooks/room'

import Toast from '@/components/Toast'
import Modal from '@/components/Modal'
import Navbar from '@/components/Navbar'

let updateToastsInterval

export default defineComponent({
  components: {
    Toast,
    Modal,
    Navbar
  },

  setup () {
    // Setup
    const route = useRoute()
    const router = useRouter()
    const appState = useAppState()
    provide('appState', appState)

    // Toasts
    const { openModal, closeModal, modalOpen } = useModal()
    clearInterval(updateToastsInterval)
    const toastUpdateInterval = 100
    updateToastsInterval = setInterval(() => {
      const toast = appState.toasts[0]
      if (!toast) return

      toast.aliveFor -= toastUpdateInterval
      if (toast.aliveFor <= 0) {
        const index = appState.toasts.indexOf(toast)
        appState.toasts.splice(index, 1)
      }
    }, toastUpdateInterval)

    // Reconnect
    {
      const {
        on,
        off,
        findCachedRoom,
        connect,
        rejoinRoom
      } = useHost()

      const code = findCachedRoom()
      if (code) {
        openModal('reconnectLoader', { hideClose: true })

        const roomRejoinedHandler = () => {
          closeModal()
          router.push({ name: 'room' })
        }
        onMounted(() => {
          on('error', closeModal)
          on('roomRejoined', roomRejoinedHandler)
        })
        onUnmounted(() => {
          off(closeModal)
          off(roomRejoinedHandler)
        })
        
        const reconnect = async () => {
          try {
            await connect()
            rejoinRoom(code)
          } catch (err) {
            console.warn(err)
          }
        }
        reconnect()
      }
    }
    {
      const {
        on,
        off,
        findCachedRoom,
        connect,
        rejoinRoom
      } = useJoin()
      const { characters } = useCharacters()

      const cached = findCachedRoom()
      let character
      if (cached && (character = characters.find((c) => c.id === cached.characterId))) {
        openModal('reconnectLoader', { hideClose: true })

        const roomRejoinedHandler = () => {
          closeModal()
          router.push({ name: 'playCharacter', params: { id: cached.characterId } })
        }
        onMounted(() => {
          on('error', closeModal)
          on('roomRejoined', roomRejoinedHandler)
        })
        onUnmounted(() => {
          off(closeModal)
          off(roomRejoinedHandler)
        })

        const reconnect = async () => {
          try {
            await connect()
            rejoinRoom(cached.code, character)
          } catch (err) {
            console.warn(err)
          }
        }
        reconnect()
      }
    }

    return {
      route,
      appState,
      closeModal,
      modalOpen
    }
  }
})
</script>

<style lang="sass">
@charset 'utf-8'

@import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&family=Playfair+Display:wght@400;700&family=Roboto:wght@500&display=swap')
@import url('https://fonts.googleapis.com/icon?family=Material+Icons')
@import './bulma'

:root
  --primary: #{$primary}

body
  background-color: #fafafa
  width: 100%
  min-height: 100vh
  overflow-x: hidden

#app
  -webkit-font-smoothing: antialiased
  -moz-osx-font-smoothing: grayscale

hr:not([class])
  border: none
  height: 8px
  background-image: url('../public/images/ruleFlourish.svg')
  background-size: contain
  background-position: center
  background-repeat: no-repeat
  position: relative
  opacity: 0.15
  &::before,
  &::after
    content: ''
    display: block
    width: 46%
    height: 1px
    background: #000
    position: absolute
    top: 50%
  &::before
    left: 0
  &::after
    right: 0

.modal-close
  &::before,
  &::after
    background-color: var(--primary)
  &:hover
    background-color: var(--primary)
    &::before,
    &::after
      background-color: #fff

.modal-background
  backdrop-filter: blur(1px)

.modal-content
  overflow: visible

[disabled]
  pointer-events: none
  
input[type='number']
  text-align: center
  width: 10em
  &.isShort
    width: 6em
  &.is-small
    width: 6em

.toastContainer
  position: fixed
  top: 1rem
  left: 1rem
  width: 350px
  z-index: 101
  pointer-events: none
  display: flex
  flex-direction: column
  > * 
    width: 100%
    pointer-events: auto
    margin-bottom: 0.25rem !important
    transition: all 0.4s ease

.toast-enter-from
  opacity: 0
  transform: translateY(30px)

.toast-leave-to
  opacity: 0
  transform: translateY(-30px)

.toast-move
  transition: transform 0.4s ease

.toast-leave-active
  position: absolute

.icon
  font-family: 'Material Icons'
  display: inline-flex
  align-items: center
  justify-content: center
  &::after
    content: attr(data-icon)
    display: inline-flex

.button.is-small
  font-family: 'Roboto'

.sortable-placeholder
  display: block
  height: 3rem
  background: rgba(#000, 0.05)
  &:not(:last-child)
    border-bottom: solid 1px #dfdfdf

@media screen and (max-width: 1023px)
  .container
    padding: 0 0.5rem !important
  
@media screen and (max-width: 767px)
  .section
    padding: 1.5rem 0 !important

.reconnectLoader
  width: 4rem
  height: 4rem
  border-width: 4px
  border-bottom-color: var(--primary)
  border-left-color: var(--primary)
  margin: auto
</style>
